import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { SmartBetaStatus } from '../../../services/smartBetaApi';
import { stateOf } from './RebalancePanel';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(14, 203, 129, 0.55); }
  70% { box-shadow: 0 0 0 7px rgba(14, 203, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(14, 203, 129, 0); }
`;

const Dot = styled.span<{ $color: string; $live: boolean }>`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  background: ${(p) => p.$color};
  ${(p) =>
    p.$live &&
    css`
      animation: ${pulse} 1.6s infinite;
    `}
`;

interface Props {
  status: SmartBetaStatus | null;
  nextRebalance: string | null;
  /** Nothing saved yet: there is no bot to speak of. */
  configured: boolean;
}

/** One glance: is the bot running, doing what — or when it runs next. */
export function botLine(status: SmartBetaStatus | null, nextRebalance: string | null, configured: boolean) {
  const state = stateOf(status);
  const exec = status?.execution?.status;
  if (exec === 'STOPPING') return { title: 'Stopping', sub: 'Cancelling open orders', color: '#F0B90B', live: true };
  if (state.running) return { title: state.label, sub: 'Bot running', color: '#0ECB81', live: true };
  if (!configured)
    return { title: 'Not set up', sub: 'Choose a model or build a basket', color: '#5E6673', live: false };
  if (status?.rebalanceToday)
    return { title: 'Idle', sub: 'Rebalances today — starts at 09:20', color: '#3B82F6', live: false };
  if (status?.progress?.phase === 'done') {
    return {
      title: `Rebalanced ${status.progress.day}`,
      sub: nextRebalance ? `Next run ${nextRebalance}` : 'Not scheduled',
      color: '#0ECB81',
      live: false,
    };
  }
  return {
    title: 'Idle',
    sub: nextRebalance ? `Next run ${nextRebalance}` : 'Not scheduled',
    color: '#5E6673',
    live: false,
  };
}

const BotStatus: React.FC<Props> = ({ status, nextRebalance, configured }) => {
  const line = botLine(status, nextRebalance, configured);
  return (
    <div role="status" aria-label={`Bot: ${line.title}`}>
      <div className="label">Bot</div>
      <div className="value" style={{ display: 'flex', alignItems: 'center' }}>
        <Dot $color={line.color} $live={line.live} />
        {line.title}
      </div>
      <div style={{ fontSize: 12, color: '#848E9C' }}>{line.sub}</div>
    </div>
  );
};

export default BotStatus;
